import { Select, SelectProps } from 'antd'
import { useEffect, useState } from 'react'
import { apiGetListUsers } from '../../api'
import defaultParams from '../../assets/constants/default_params'
import { filterOptionByLabel } from '../../utils/filter-utils'
import { IUser } from '../../models/user.model'

let cachedUsers: IUser[] | null = null
let cachedOptions: { label: string, value: number }[] | null = null
let cachedById: Map<number, IUser> | null = null
let inFlight: Promise<IUser[]> | null = null

const loadUsers = async (): Promise<IUser[]> => {
    if (cachedUsers) return cachedUsers
    if (inFlight) return inFlight
    inFlight = apiGetListUsers({ limit: defaultParams.MAX_LIMIT }).then((resp: any) => {
        const users: IUser[] = resp?.data?.users ?? []
        cachedUsers = users
        cachedOptions = users.map(u => ({
            label: `${u.full_name ?? u.username ?? u.id} (${u.username ?? ''})`,
            value: Number(u.id),
        }))
        cachedById = new Map(users.map(u => [Number(u.id), u]))
        return users
    }).finally(() => {
        inFlight = null
    })
    return inFlight
}

// Shared with outlet list Sale column etc. — reuse module cache to avoid a
// second /users fetch per page. Returns Map<userId, IUser>.
export const loadUserMap = async (): Promise<Map<number, IUser>> => {
    if (cachedById) return cachedById
    await loadUsers()
    return cachedById ?? new Map()
}

type Props = Omit<SelectProps, 'options' | 'filterOption'> & {
    allowClear?: boolean
    multiple?: boolean
}

export default function UserSelect({ multiple, ...rest }: Props) {
    const [options, setOptions] = useState<{ label: string, value: number }[]>(cachedOptions ?? [])

    useEffect(() => {
        let mounted = true
        loadUsers().then(() => {
            if (mounted && cachedOptions) setOptions(cachedOptions)
        })
        return () => { mounted = false }
    }, [])

    return (
        <Select
            {...rest}
            mode={multiple ? 'multiple' : undefined}
            showSearch
            allowClear
            options={options}
            filterOption={filterOptionByLabel}
            style={{ minWidth: 200, ...(rest.style ?? {}) }}
        />
    )
}
